import { Card } from '@/shared/components/ui/Card';
import type { MessageKey } from '@/shared/i18n/messages';
import { useI18n } from '@/shared/i18n/useT';

type FunctionCategory = 'logical' | 'math' | 'statistical' | 'text' | 'information' | 'lookup' | 'date' | 'variables';

interface FunctionDoc {
  readonly name: string;
  readonly category: FunctionCategory;
  readonly syntaxKey: MessageKey;
  readonly descriptionKey: MessageKey;
}

interface FunctionGroup {
  readonly id: FunctionCategory;
  readonly labelKey: MessageKey;
}

/**
 * The function names are identifiers, never translated; only the syntax
 * placeholders, the descriptions and the category labels come from the
 * message catalog.
 */
const FUNCTION_GROUPS: readonly FunctionGroup[] = [
  { id: 'logical', labelKey: 'help.formulas.categories.logical' },
  { id: 'math', labelKey: 'help.formulas.categories.math' },
  { id: 'statistical', labelKey: 'help.formulas.categories.statistical' },
  { id: 'text', labelKey: 'help.formulas.categories.text' },
  { id: 'information', labelKey: 'help.formulas.categories.information' },
  { id: 'lookup', labelKey: 'help.formulas.categories.lookup' },
  { id: 'date', labelKey: 'help.formulas.categories.date' },
  { id: 'variables', labelKey: 'help.formulas.categories.variables' },
];

const FUNCTION_DOCS: readonly FunctionDoc[] = [
  { name: 'IF', category: 'logical', syntaxKey: 'help.formulas.functions.IF.syntax', descriptionKey: 'help.formulas.functions.IF.description' },
  { name: 'IFS', category: 'logical', syntaxKey: 'help.formulas.functions.IFS.syntax', descriptionKey: 'help.formulas.functions.IFS.description' },
  { name: 'IFERROR', category: 'logical', syntaxKey: 'help.formulas.functions.IFERROR.syntax', descriptionKey: 'help.formulas.functions.IFERROR.description' },
  { name: 'NOT', category: 'logical', syntaxKey: 'help.formulas.functions.NOT.syntax', descriptionKey: 'help.formulas.functions.NOT.description' },
  { name: 'TRUE', category: 'logical', syntaxKey: 'help.formulas.functions.TRUE.syntax', descriptionKey: 'help.formulas.functions.TRUE.description' },
  { name: 'FALSE', category: 'logical', syntaxKey: 'help.formulas.functions.FALSE.syntax', descriptionKey: 'help.formulas.functions.FALSE.description' },
  { name: 'NULL', category: 'logical', syntaxKey: 'help.formulas.functions.NULL.syntax', descriptionKey: 'help.formulas.functions.NULL.description' },
  { name: 'ABS', category: 'math', syntaxKey: 'help.formulas.functions.ABS.syntax', descriptionKey: 'help.formulas.functions.ABS.description' },
  { name: 'SUM', category: 'math', syntaxKey: 'help.formulas.functions.SUM.syntax', descriptionKey: 'help.formulas.functions.SUM.description' },
  { name: 'SUMIF', category: 'math', syntaxKey: 'help.formulas.functions.SUMIF.syntax', descriptionKey: 'help.formulas.functions.SUMIF.description' },
  { name: 'SUMIFS', category: 'math', syntaxKey: 'help.formulas.functions.SUMIFS.syntax', descriptionKey: 'help.formulas.functions.SUMIFS.description' },
  { name: 'COST', category: 'math', syntaxKey: 'help.formulas.functions.COST.syntax', descriptionKey: 'help.formulas.functions.COST.description' },
  { name: 'AVERAGE', category: 'statistical', syntaxKey: 'help.formulas.functions.AVERAGE.syntax', descriptionKey: 'help.formulas.functions.AVERAGE.description' },
  { name: 'AVERAGEIF', category: 'statistical', syntaxKey: 'help.formulas.functions.AVERAGEIF.syntax', descriptionKey: 'help.formulas.functions.AVERAGEIF.description' },
  { name: 'AVERAGEIFS', category: 'statistical', syntaxKey: 'help.formulas.functions.AVERAGEIFS.syntax', descriptionKey: 'help.formulas.functions.AVERAGEIFS.description' },
  { name: 'COUNT', category: 'statistical', syntaxKey: 'help.formulas.functions.COUNT.syntax', descriptionKey: 'help.formulas.functions.COUNT.description' },
  { name: 'COUNTA', category: 'statistical', syntaxKey: 'help.formulas.functions.COUNTA.syntax', descriptionKey: 'help.formulas.functions.COUNTA.description' },
  { name: 'COUNTIF', category: 'statistical', syntaxKey: 'help.formulas.functions.COUNTIF.syntax', descriptionKey: 'help.formulas.functions.COUNTIF.description' },
  { name: 'COUNTIFS', category: 'statistical', syntaxKey: 'help.formulas.functions.COUNTIFS.syntax', descriptionKey: 'help.formulas.functions.COUNTIFS.description' },
  { name: 'MAX', category: 'statistical', syntaxKey: 'help.formulas.functions.MAX.syntax', descriptionKey: 'help.formulas.functions.MAX.description' },
  { name: 'MAXIF', category: 'statistical', syntaxKey: 'help.formulas.functions.MAXIF.syntax', descriptionKey: 'help.formulas.functions.MAXIF.description' },
  { name: 'MAXIFS', category: 'statistical', syntaxKey: 'help.formulas.functions.MAXIFS.syntax', descriptionKey: 'help.formulas.functions.MAXIFS.description' },
  { name: 'MIN', category: 'statistical', syntaxKey: 'help.formulas.functions.MIN.syntax', descriptionKey: 'help.formulas.functions.MIN.description' },
  { name: 'MINIF', category: 'statistical', syntaxKey: 'help.formulas.functions.MINIF.syntax', descriptionKey: 'help.formulas.functions.MINIF.description' },
  { name: 'MINIFS', category: 'statistical', syntaxKey: 'help.formulas.functions.MINIFS.syntax', descriptionKey: 'help.formulas.functions.MINIFS.description' },
  { name: 'CHAR', category: 'text', syntaxKey: 'help.formulas.functions.CHAR.syntax', descriptionKey: 'help.formulas.functions.CHAR.description' },
  { name: 'TEXT', category: 'text', syntaxKey: 'help.formulas.functions.TEXT.syntax', descriptionKey: 'help.formulas.functions.TEXT.description' },
  { name: 'ISNUMBER', category: 'information', syntaxKey: 'help.formulas.functions.ISNUMBER.syntax', descriptionKey: 'help.formulas.functions.ISNUMBER.description' },
  { name: 'ISTEXT', category: 'information', syntaxKey: 'help.formulas.functions.ISTEXT.syntax', descriptionKey: 'help.formulas.functions.ISTEXT.description' },
  { name: 'ISDATE', category: 'information', syntaxKey: 'help.formulas.functions.ISDATE.syntax', descriptionKey: 'help.formulas.functions.ISDATE.description' },
  { name: 'ISBLANK', category: 'information', syntaxKey: 'help.formulas.functions.ISBLANK.syntax', descriptionKey: 'help.formulas.functions.ISBLANK.description' },
  { name: 'ISERROR', category: 'information', syntaxKey: 'help.formulas.functions.ISERROR.syntax', descriptionKey: 'help.formulas.functions.ISERROR.description' },
  { name: 'ISNULL', category: 'information', syntaxKey: 'help.formulas.functions.ISNULL.syntax', descriptionKey: 'help.formulas.functions.ISNULL.description' },
  { name: 'XLOOKUP', category: 'lookup', syntaxKey: 'help.formulas.functions.XLOOKUP.syntax', descriptionKey: 'help.formulas.functions.XLOOKUP.description' },
  { name: 'FILTER', category: 'lookup', syntaxKey: 'help.formulas.functions.FILTER.syntax', descriptionKey: 'help.formulas.functions.FILTER.description' },
  { name: 'TAKE', category: 'lookup', syntaxKey: 'help.formulas.functions.TAKE.syntax', descriptionKey: 'help.formulas.functions.TAKE.description' },
  { name: 'SLICE', category: 'lookup', syntaxKey: 'help.formulas.functions.SLICE.syntax', descriptionKey: 'help.formulas.functions.SLICE.description' },
  { name: 'UPTO', category: 'lookup', syntaxKey: 'help.formulas.functions.UPTO.syntax', descriptionKey: 'help.formulas.functions.UPTO.description' },
  { name: 'ROW', category: 'lookup', syntaxKey: 'help.formulas.functions.ROW.syntax', descriptionKey: 'help.formulas.functions.ROW.description' },
  { name: 'ROWS', category: 'lookup', syntaxKey: 'help.formulas.functions.ROWS.syntax', descriptionKey: 'help.formulas.functions.ROWS.description' },
  { name: 'TODAY', category: 'date', syntaxKey: 'help.formulas.functions.TODAY.syntax', descriptionKey: 'help.formulas.functions.TODAY.description' },
  { name: 'DATE', category: 'date', syntaxKey: 'help.formulas.functions.DATE.syntax', descriptionKey: 'help.formulas.functions.DATE.description' },
  { name: 'FXRATE', category: 'lookup', syntaxKey: 'help.formulas.functions.FXRATE.syntax', descriptionKey: 'help.formulas.functions.FXRATE.description' },
  { name: 'LET', category: 'variables', syntaxKey: 'help.formulas.functions.LET.syntax', descriptionKey: 'help.formulas.functions.LET.description' },
];

const GROUPED_FUNCTIONS = FUNCTION_GROUPS.map((group) => ({
  ...group,
  docs: FUNCTION_DOCS
    .filter((fn) => fn.category === group.id)
    .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0)),
}));


export function FormulasSection() {
  const { t } = useI18n();

  return (
    <div className="space-y-4">
      <Card>
        <p className="text-sm leading-relaxed text-muted">{t('help.formulas.intro')}</p>
      </Card>

      <Card title={t('help.formulas.whereTitle')}>
        <ul className="space-y-3 text-sm leading-relaxed text-muted">
          <li>{t('help.formulas.whereColumn')}</li>
          <li>{t('help.formulas.whereCell')}</li>
          <li>{t('help.formulas.whereOverride')}</li>
        </ul>
      </Card>

      <Card
        title={t('help.formulas.functionsTitle')}
        description={t('help.formulas.functionsIntro')}
      >
        {/* Phones get a stacked list; a three-column table would be too tight at 375px. */}
        <div className="sm:hidden">
          {GROUPED_FUNCTIONS.map((group) => (
            <section key={group.id} className="mt-5 first:mt-0">
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted">
                {t(group.labelKey)}
              </h3>
              <ul className="space-y-3">
                {group.docs.map((fn) => (
                  <li key={fn.name} className="min-w-0 rounded-token border border-border bg-surface p-3">
                    <p className="font-mono text-xs font-semibold text-fg">{t(fn.syntaxKey)}</p>
                    <p className="mt-1 break-words text-sm leading-relaxed text-muted">
                      {t(fn.descriptionKey)}
                    </p>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>

        <div className="hidden overflow-x-auto sm:block">
          <table className="w-full border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-border">
                <th scope="col" className="pb-2 pr-3 font-semibold text-fg">
                  {t('help.formulas.functionName')}
                </th>
                <th scope="col" className="pb-2 pr-3 font-semibold text-fg">
                  {t('help.formulas.functionSyntax')}
                </th>
                <th scope="col" className="pb-2 font-semibold text-fg">
                  {t('help.formulas.functionDescription')}
                </th>
              </tr>
            </thead>
            {GROUPED_FUNCTIONS.map((group) => (
              <tbody key={group.id}>
                <tr>
                  <th
                    scope="rowgroup"
                    colSpan={3}
                    className="pb-1.5 pt-5 text-left text-xs font-semibold uppercase tracking-wide text-muted"
                  >
                    {t(group.labelKey)}
                  </th>
                </tr>
                {group.docs.map((fn) => (
                  <tr key={fn.name} className="border-b border-border/60 align-top">
                    <th scope="row" className="whitespace-nowrap py-2.5 pr-3 font-mono text-xs font-semibold text-fg">
                      {fn.name}
                    </th>
                    <td className="min-w-0 py-2.5 pr-3 font-mono text-xs leading-relaxed text-fg">
                      {t(fn.syntaxKey)}
                    </td>
                    <td className="min-w-0 py-2.5 text-sm leading-relaxed text-muted">
                      {t(fn.descriptionKey)}
                    </td>
                  </tr>
                ))}
              </tbody>
            ))}
          </table>
        </div>
      </Card>

      <Card title={t('help.formulas.operatorsTitle')}>
        <p className="text-sm leading-relaxed text-muted">{t('help.formulas.operatorsIntro')}</p>
        <ul className="mt-3 space-y-2 text-sm leading-relaxed text-muted">
          <li>{t('help.formulas.operatorArithmetic')}</li>
          <li>{t('help.formulas.operatorPercent')}</li>
          <li>{t('help.formulas.operatorConcat')}</li>
          <li>{t('help.formulas.operatorCompare')}</li>
        </ul>
        <p className="mt-3 text-sm leading-relaxed text-muted">
          {t('help.formulas.operatorPrecedence')}
        </p>
      </Card>

      <Card title={t('help.formulas.referencesTitle')}>
        <p className="text-sm leading-relaxed text-muted">{t('help.formulas.referencesIntro')}</p>
        <ul className="mt-3 space-y-2 text-sm leading-relaxed text-muted">
          <li>{t('help.formulas.referenceColumn')}</li>
          <li>{t('help.formulas.referenceCurrent')}</li>
          <li>{t('help.formulas.referenceRow')}</li>
          <li>{t('help.formulas.referenceRange')}</li>
          <li>{t('help.formulas.referenceSheet')}</li>
          <li>{t('help.formulas.referenceQuoting')}</li>
          <li>{t('help.formulas.referenceCrossDocument')}</li>
        </ul>
      </Card>

      <Card title={t('help.formulas.valuesTitle')}>
        <p className="text-sm leading-relaxed text-muted">{t('help.formulas.valuesIntro')}</p>
        <ul className="mt-3 space-y-2 text-sm leading-relaxed text-muted">
          <li>{t('help.formulas.valuesEmpty')}</li>
          <li>{t('help.formulas.valuesErrors')}</li>
          <li>{t('help.formulas.valuesRaw')}</li>
        </ul>
      </Card>

      <Card title={t('help.formulas.limitsTitle')}>
        <p className="text-sm leading-relaxed text-muted">{t('help.formulas.limitsBody')}</p>
        <p className="mt-3 text-sm leading-relaxed text-muted">{t('help.formulas.limitsRollups')}</p>
        <p className="mt-3 text-sm leading-relaxed text-muted">{t('help.formulas.limitsNames')}</p>
      </Card>
    </div>
  );
}
